import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import pedidoVentaService, {
  PedidoVenta,
  PedidoVentaDetalle,
  TenantRelacionadoPedido,
} from '../../../services/pedidoVentaService';
import { getApiErrorMessage } from '../../../utils/apiError';
import { formatCurrency } from '../../../utils/formatters';
import {
  ConversionLineaState,
  aplicarCantidad,
  aplicarCantidadB,
  aplicarPorcentaje,
  repartirLineaConversion,
} from '../../../utils/pedidoConversionCantidades';
import { aplicarConversionMaestrosConFloor } from '../../../services/pedidoVentaConversionMaestros';

type LineaEditable = ConversionLineaState & {
  key: string;
  nroItem: number;
  codigoArticulo: string;
  descripcionArticulo: string;
  precioUnitario: number;
  detalleOriginal: PedidoVentaDetalle;
};

interface ConversionPedidoDialogProps {
  open: boolean;
  pedidoId: number;
  onClose: () => void;
  onDone: () => void;
}

const etiquetaTenant = (tenant: TenantRelacionadoPedido): string => {
  const nombre = tenant.name || tenant.code || `Empresa ${tenant.tenantId}`;
  const codigo = tenant.code && tenant.code !== nombre ? ` (${tenant.code})` : '';
  const rol = tenant.rol ? ` — ${tenant.rol}` : '';
  return `${nombre}${codigo}${rol}`;
};

const mapLineasFromPedido = (data: PedidoVenta): LineaEditable[] =>
  (data.detalles ?? []).map((d, idx) => {
    const state = repartirLineaConversion(
      d.cantidad,
      d.cantidadB,
      d.porcCantB ?? d.porcSubtotalCantB,
    );
    return {
      ...state,
      key: `${d.nroItem ?? idx}-${d.codigoArticulo ?? ''}`,
      nroItem: d.nroItem || idx + 1,
      codigoArticulo: String(d.codigoArticulo ?? '').trim(),
      descripcionArticulo: String(d.descripcionArticulo ?? '').trim(),
      precioUnitario: Number(d.precioUnitarioFinal ?? d.precioUnitario ?? 0),
      detalleOriginal: {
        ...d,
        cantidad: state.cantidad,
        cantidadB: state.cantidadB,
        porcCantB: state.porcentaje,
        porcSubtotalCantB: state.porcentaje,
      },
    };
  });

const pedidoConLineasFloor = (pedido: PedidoVenta, lineas: LineaEditable[]): PedidoVenta => {
  const detalles: PedidoVentaDetalle[] = lineas.map((l) => ({
    ...l.detalleOriginal,
    nroItem: l.nroItem,
    codigoArticulo: l.codigoArticulo,
    descripcionArticulo: l.descripcionArticulo,
    cantidad: l.cantidad,
    cantidadB: l.cantidadB,
    porcCantB: l.porcentaje,
    porcSubtotalCantB: l.porcentaje,
    subtotal: Number(l.detalleOriginal.precioUnitario ?? l.precioUnitario ?? 0) * l.cantidad,
  }));
  const importeTotal = detalles.reduce((acc, d) => {
    const sub = d.subtotal ?? Number(d.precioUnitario ?? 0) * Number(d.cantidad ?? 0);
    return acc + sub;
  }, 0);
  return { ...pedido, detalles, importeTotal };
};
const ConversionPedidoDialog: React.FC<ConversionPedidoDialogProps> = ({
  open,
  pedidoId,
  onClose,
  onDone,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applyingMaestros, setApplyingMaestros] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pedido, setPedido] = useState<PedidoVenta | null>(null);
  const [pedidoSnapshot, setPedidoSnapshot] = useState<PedidoVenta | null>(null);
  const [lineas, setLineas] = useState<LineaEditable[]>([]);
  const [tenants, setTenants] = useState<TenantRelacionadoPedido[]>([]);
  const [tenantIds, setTenantIds] = useState<number[]>([]);
  const [resultadoEnvio, setResultadoEnvio] = useState<{ ok: boolean; msg: string }[] | null>(null);

  const busy = saving || applyingMaestros;

  useEffect(() => {
    if (!open || pedidoId <= 0) return;
    let cancelled = false;
    setError(null);
    setResultadoEnvio(null);
    setApplyingMaestros(false);
    setLoading(true);
    void (async () => {
      try {
        const [data, listaTenants] = await Promise.all([
          pedidoVentaService.getPedidoVenta(pedidoId, { sinCache: true }),
          pedidoVentaService.getTenantsRelacionados().catch(() => [] as TenantRelacionadoPedido[]),
        ]);
        if (cancelled) return;
        setPedido(data);
        // Snapshot previo a cualquier aplicar-maestros / generar (fallback de rollback)
        setPedidoSnapshot(JSON.parse(JSON.stringify(data)) as PedidoVenta);
        setTenants(listaTenants);
        setTenantIds(listaTenants.length === 1 ? [listaTenants[0].tenantId] : []);
        setLineas(mapLineasFromPedido(data));
      } catch (err: unknown) {
        if (!cancelled) {
          setError(getApiErrorMessage(err) || 'No se pudo cargar el pedido para conversión.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, pedidoId]);

  const tieneCantidadB = useMemo(
    () => lineas.some((l) => l.cantidadB > 0),
    [lineas],
  );

  const totalImporteOrigen = useMemo(
    () => lineas.reduce((acc, l) => acc + l.precioUnitario * l.cantidad, 0),
    [lineas],
  );

  const updateLinea = (key: string, next: ConversionLineaState) => {
    setLineas((prev) => prev.map((l) => (l.key === key ? { ...l, ...next } : l)));
  };

  const toggleTenant = (id: number) => {
    setTenantIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const restaurarPedido = async () => {
    if (!pedidoSnapshot) return;
    try {
      await pedidoVentaService.updatePedidoVenta(pedidoId, pedidoSnapshot, {
        preservarPrecios: true,
      });
      setPedido(pedidoSnapshot);
      setLineas(mapLineasFromPedido(pedidoSnapshot));
    } catch {
      enqueueSnackbar(
        'No se pudo revertir el pedido tras el error. Revisá el pedido manualmente.',
        { variant: 'warning', autoHideDuration: 6000 },
      );
    }
  };

  const handleAplicarMaestros = async () => {
    try {
      setApplyingMaestros(true);
      setError(null);
      const { pedido: corregido, tieneCantidadB: conB } =
        await aplicarConversionMaestrosConFloor(pedidoId);
      setPedido(corregido);
      setLineas(mapLineasFromPedido(corregido));
      if (conB) {
        enqueueSnackbar('Se aplicó el % de cliente/proveedor (redondeo hacia abajo).', {
          variant: 'success',
        });
      } else {
        enqueueSnackbar(
          'Con redondeo hacia abajo no quedó Cantidad B (p. ej. 2 uds al 30%). Revisá el % o la cantidad.',
          { variant: 'warning', autoHideDuration: 6000 },
        );
      }
    } catch (err: unknown) {
      setError(
        getApiErrorMessage(err) ||
          'No se pudo aplicar el porcentaje de cliente/proveedor.',
      );
    } finally {
      setApplyingMaestros(false);
    }
  };

  const handleGenerar = async () => {
    if (!pedido || !tieneCantidadB || tenantIds.length === 0) return;
    let actualizado = false;
    try {
      setSaving(true);
      setError(null);
      const payload = pedidoConLineasFloor(pedido, lineas);

      await pedidoVentaService.updatePedidoVenta(pedidoId, payload, { preservarPrecios: true });
      actualizado = true;

      const copia = await pedidoVentaService.copiarRelacionados(pedidoId, tenantIds);
      const mensajes = copia.destinos.map((d) => ({
        ok: d.ok,
        msg: d.ok
          ? `${d.tenantCode || `Empresa ${d.tenantId}`}: pedido ${d.nroFormulario ?? '—'}`
          : `${d.tenantCode || `Empresa ${d.tenantId}`}: ${d.error || 'Error'}`,
      }));
      setResultadoEnvio(mensajes);
      const ok = copia.destinos.filter((d) => d.ok);
      if (ok.length > 0) {
        enqueueSnackbar(
          ok.length === 1
            ? `Pedido B enviado a ${ok[0].tenantCode || 'la empresa relacionada'}.`
            : `Pedido B enviado a ${ok.length} empresas.`,
          { variant: ok.length < copia.destinos.length ? 'warning' : 'success' },
        );
        onDone();
      } else {
        await restaurarPedido();
        enqueueSnackbar('No se pudo enviar el pedido B. Se revirtió el reparto en el origen.', {
          variant: 'error',
        });
        setError('El envío falló en todos los destinos. El pedido origen volvió a su estado anterior.');
      }
    } catch (err: unknown) {
      if (actualizado) {
        await restaurarPedido();
        setError(
          `${getApiErrorMessage(err) || 'No se pudo generar la conversión.'} Se revirtió el pedido origen.`,
        );
      } else {
        setError(getApiErrorMessage(err) || 'No se pudo generar la conversión.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      // Por encima del diálogo de detalle del pedido
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>Conversión</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Usá <strong>Aplicar % de maestros</strong> para cargar el porcentaje del cliente (o del
              proveedor del artículo si el cliente no tiene). Después podés ajustar Cantidad /
              Cant. B / % a mano. Al generar se guarda el reparto y se envía la Cantidad B. Si el
              envío falla, se revierte el pedido origen.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            {resultadoEnvio && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {resultadoEnvio.map((item) => (
                  <Alert key={item.msg} severity={item.ok ? 'success' : 'error'}>
                    {item.msg}
                  </Alert>
                ))}
              </Box>
            )}
            {!resultadoEnvio && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => void handleAplicarMaestros()}
                    disabled={busy || loading || lineas.length === 0}
                    startIcon={applyingMaestros ? <CircularProgress size={16} /> : undefined}
                    sx={{ textTransform: 'none' }}
                  >
                    {applyingMaestros ? 'Aplicando…' : 'Aplicar % de maestros'}
                  </Button>
                </Box>

                <TableContainer sx={{ maxHeight: 360 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 180 }}>Artículo</TableCell>
                        <TableCell align="right" sx={{ width: 88 }}>
                          Total
                        </TableCell>
                        <TableCell sx={{ width: 128 }}>Cantidad</TableCell>
                        <TableCell sx={{ width: 128 }}>Cant. B</TableCell>
                        <TableCell sx={{ width: 112 }}>% Conv.</TableCell>
                        <TableCell align="right" sx={{ width: 120, whiteSpace: 'nowrap' }}>
                          Imp. origen
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lineas.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            Sin artículos
                          </TableCell>
                        </TableRow>
                      )}
                      {lineas.map((linea) => {
                        const importeOrigen = linea.precioUnitario * linea.cantidad;
                        return (
                          <TableRow key={linea.key}>
                            <TableCell sx={{ maxWidth: 220 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {linea.codigoArticulo || '—'}
                              </Typography>
                              {linea.descripcionArticulo ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {linea.descripcionArticulo}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600}>
                                {linea.total}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                fullWidth
                                value={linea.cantidad}
                                disabled={busy}
                                onChange={(e) =>
                                  updateLinea(
                                    linea.key,
                                    aplicarCantidad(linea, Number(e.target.value)),
                                  )
                                }
                                inputProps={{ min: 0, max: linea.total, step: 1 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                fullWidth
                                value={linea.cantidadB}
                                disabled={busy}
                                onChange={(e) =>
                                  updateLinea(
                                    linea.key,
                                    aplicarCantidadB(linea, Number(e.target.value)),
                                  )
                                }
                                inputProps={{ min: 0, max: linea.total, step: 1 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                fullWidth
                                value={linea.porcentaje}
                                disabled={busy}
                                onChange={(e) =>
                                  updateLinea(
                                    linea.key,
                                    aplicarPorcentaje(linea, Number(e.target.value)),
                                  )
                                }
                                inputProps={{ min: 0, max: 100, step: 1 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                {formatCurrency(importeOrigen)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {lineas.length > 0 && (
                  <Typography variant="body2" color="text.secondary" textAlign="right">
                    Total origen:{' '}
                    <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {formatCurrency(totalImporteOrigen)}
                    </Box>
                  </Typography>
                )}

                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Empresa relacionada
                </Typography>
                {tenants.length === 0 ? (
                  <Alert severity="warning">
                    No hay empresas relacionadas disponibles. Configurá el tenant madre/hija antes
                    de enviar.
                  </Alert>
                ) : (
                  <FormGroup>
                    {tenants.map((tenant) => (
                      <FormControlLabel
                        key={tenant.tenantId}
                        control={
                          <Checkbox
                            checked={tenantIds.includes(tenant.tenantId)}
                            onChange={() => toggleTenant(tenant.tenantId)}
                            disabled={busy}
                          />
                        }
                        label={etiquetaTenant(tenant)}
                      />
                    ))}
                  </FormGroup>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>
          Cerrar
        </Button>
        {!resultadoEnvio && (
          <Button
            variant="contained"
            onClick={() => void handleGenerar()}
            disabled={
              busy || loading || !tieneCantidadB || tenantIds.length === 0 || lineas.length === 0
            }
            sx={{ textTransform: 'none' }}
          >
            {saving ? 'Generando…' : 'Generar conversión'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConversionPedidoDialog;
